import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { ArticleCard, coverUrl, KindBadge, type ArticleCardData } from '../components/ArticleCard';
import { useApi } from '../lib/useApi';
import { count, shortDate } from '../lib/format';
import { Button, EmptyState, ErrorBlock, LoadingBlock, TextInput } from '../components/ui';
import '../styles/public.css';
import '../styles/insights.css';
import '../styles/blog.css';

/**
 * Blog — the public content hub.
 *
 * Filters live in the URL so a category or a search can be shared and reached
 * by the back button, the same reasoning as the property search. The three
 * shelves the brief asks for (featured, popular, latest) only appear on the
 * UNFILTERED view: once someone is searching, a "popular" rail is noise
 * competing with the thing they actually asked for.
 */

interface Category { id: string; slug: string; name: string; description: string | null; articles: number }
interface Response {
  articles: ArticleCardData[];
  total: number; limit: number; offset: number;
  featured: ArticleCardData | null;
  popular: ArticleCardData[];
  categories: Category[];
}

export function Blog() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');

  const get = (k: string) => params.get(k) ?? '';
  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    next.delete('offset');
    setParams(next);
  };

  const offset = Number(get('offset') || 0);
  const list = useApi<Response>('/api/v1/content', {
    q: get('q') || undefined,
    category: get('category') || undefined,
    tag: get('tag') || undefined,
    offset: offset || undefined,
  });

  const data = list.data;
  const filtering = !!(get('q') || get('category') || get('tag'));
  /** What the API returned for this page. Pagination is arithmetic on THIS. */
  const shown = data?.articles.length ?? 0;
  const activeCategory = data?.categories.find(c => c.slug === get('category'));

  /*
   * One post, one place on the page.
   *
   * The three shelves overlap by construction: the featured post is normally
   * also the newest, and the most-read are usually recent as well. Rendering
   * all three verbatim put "How much rent should you charge in Nairobi?" on
   * the page three times — hero, Latest, and Most read — which reads as a
   * duplicated post rather than as three shelves.
   *
   * So the shelves claim in order. Featured takes its post, Latest takes what
   * is left, Most read takes what neither has already shown. On a blog this
   * size that empties Most read and the section disappears, which is correct:
   * there is nothing to re-surface when every post is already on screen. Once
   * there are more articles than fit one page it returns on its own, carrying
   * exactly the popular posts that were NOT on page one.
   *
   * `shown` above is deliberately not recomputed from these. It is the count
   * the API returned, and dedupe is a rendering decision — recomputing it
   * would move the "Show more" boundary, and would show the empty state on a
   * page whose only article happened to be the featured one.
   */
  const claimed = new Set<string>();
  if (!filtering && data?.featured) claimed.add(data.featured.id);
  const latest = (data?.articles ?? []).filter(a => !claimed.has(a.id));
  for (const a of latest) claimed.add(a.id);
  const popular = (data?.popular ?? []).filter(a => !claimed.has(a.id));

  return (
    <>
      <PublicHeader />

      <section className="ins-hero">
        <div className="ins-hero-inner">
          <p className="ins-kicker">{activeCategory ? 'Category' : 'The NyumbaSuite blog'}</p>
          <h1>{activeCategory ? activeCategory.name : 'Property, rent and running a portfolio in Kenya'}</h1>
          <p>
            {activeCategory?.description
              ?? 'Practical writing for landlords, tenants and the people who look after buildings.'}
          </p>
          <form className="ins-search" onSubmit={e => { e.preventDefault(); set({ q }); }}>
            <TextInput aria-label="Search articles" value={q}
              placeholder="Search arrears, deposits, maintenance…"
              onChange={e => setQ(e.target.value)} />
            <Button type="submit">Search</Button>
          </form>
        </div>
      </section>

      <nav className="ins-cats" aria-label="Categories">
        <button type="button" className={`ins-cat-chip${!get('category') ? ' on' : ''}`}
          onClick={() => set({ category: '' })}>
          All
        </button>
        {(data?.categories ?? []).filter(c => c.articles > 0).map(c => (
          <button key={c.slug} type="button"
            className={`ins-cat-chip${get('category') === c.slug ? ' on' : ''}`}
            onClick={() => set({ category: c.slug })}>
            {c.name} <span className="ins-cat-n">{c.articles}</span>
          </button>
        ))}
      </nav>

      <div className="ins-body">
        {list.loading ? <LoadingBlock />
          : list.error ? <ErrorBlock error={list.error} onRetry={list.reload} />
          : (
            <>
              {/* The featured slot, only when nothing is being filtered. */}
              {!filtering && data?.featured && (
                <Link to={`/blog/${data.featured.slug}`} className="ins-feature">
                  {data.featured.cover_image_id && (
                    <img src={coverUrl(data.featured.cover_image_id)} alt="" />
                  )}
                  <div className="ins-feature-text">
                    <p className="ins-card-meta">
                      <span className="ins-cat">{data.featured.category_name ?? 'Featured'}</span>
                      <KindBadge kind={data.featured.kind} sponsor={data.featured.sponsor_name} />
                    </p>
                    <h2>{data.featured.title}</h2>
                    {data.featured.summary && <p className="ins-feature-sum">{data.featured.summary}</p>}
                    <p className="ins-card-foot">
                      <span className="ins-av" aria-hidden>{data.featured.author_name.trim().charAt(0).toUpperCase()}</span>
                      <span className="ins-card-by">{data.featured.author_name}</span>
                      {data.featured.published_at && <span>{shortDate(data.featured.published_at)}</span>}
                      <span>{data.featured.reading_minutes} min read</span>
                    </p>
                  </div>
                </Link>
              )}

              {shown === 0 ? (
                <EmptyState
                  title={filtering ? 'Nothing matches that' : 'Nothing published yet'}
                  message={filtering
                    ? 'Try a different search, or browse a category above.'
                    : 'Articles will appear here as they are published.'}
                  action={filtering
                    ? <Button variant="secondary" onClick={() => { setQ(''); setParams(new URLSearchParams()); }}>
                        Clear the filters
                      </Button>
                    : undefined} />
              ) : (
                <>
                  <div className="ins-section-head">
                    <h2>{filtering ? `${count(data!.total)} article${data!.total === 1 ? '' : 's'}` : 'Latest'}</h2>
                  </div>
                  <div className="ins-grid">
                    {latest.map(a => <ArticleCard key={a.id} article={a} />)}
                  </div>

                  {data!.total > offset + shown && (
                    <div className="ins-more">
                      <Button variant="secondary"
                        onClick={() => set({ offset: String(offset + data!.limit) })}>
                        Show more
                      </Button>
                    </div>
                  )}
                </>
              )}

              {!filtering && popular.length > 0 && (
                <section className="ins-popular">
                  <div className="ins-section-head"><h2>Most read</h2></div>
                  <div className="ins-grid">
                    {popular.map(a => <ArticleCard key={a.id} article={a} />)}
                  </div>
                </section>
              )}
            </>
          )}
      </div>

      {/* The other half of a content hub: readers have stories worth printing. */}
      <section className="ins-cta">
        <h2>Have a story worth sharing?</h2>
        <p>
          Landlords, tenants and professionals — tell us what you have learned running,
          renting or servicing property in Kenya.
        </p>
        <Link className="btn btn-primary" to="/blog/share-your-story">Share your story</Link>
      </section>

      <SiteFooter />
    </>
  );
}
